import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Alert } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { CopyButton } from "../../components/ui/CopyButton";
import { PageHeader } from "../../components/ui/PageHeader";
import { StatTile } from "../../components/ui/StatTile";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { ApiError } from "../../lib/api";
import {
  type EnrollmentTokenSummary,
  type IssuedToken,
  enrollmentApi,
} from "../../lib/api/enrollment";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

import { CreateTokensForm } from "./CreateTokensForm";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function EnrollmentBatchDetailPage() {
  useLocale();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [justIssued, setJustIssued] = useState<IssuedToken[]>([]);

  const batch = useQuery({
    queryKey: ["enrollment", "batch", id],
    queryFn: () => enrollmentApi.getBatch(id!),
    enabled: Boolean(id),
  });

  const tokens = useQuery({
    queryKey: ["enrollment", "batch", id, "tokens"],
    queryFn: () => enrollmentApi.listTokens(id!),
    enabled: Boolean(id),
  });

  if (!id) return null;

  const columns: Column<EnrollmentTokenSummary>[] = [
    {
      key: "short_code",
      header: t("enrollment.tokens.column.shortCode"),
      render: (row) => row.short_code || "—",
    },
    {
      key: "expires_at",
      header: t("enrollment.tokens.column.expiresAt"),
      render: (row) => formatDateTime(row.expires_at),
    },
    {
      key: "consumed_at",
      header: t("enrollment.tokens.column.consumedAt"),
      render: (row) =>
        row.consumed_at ? (
          <Badge tone="info">{formatDateTime(row.consumed_at)}</Badge>
        ) : (
          <Badge tone="neutral">—</Badge>
        ),
    },
    {
      key: "user",
      header: t("enrollment.tokens.column.user"),
      render: (row) => row.user_full_name || row.user_email || (row.user ? row.user : "—"),
    },
    {
      key: "created_at",
      header: t("enrollment.tokens.column.createdAt"),
      render: (row) => formatDateTime(row.created_at),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t("enrollment.detail.eyebrow")}
        title={t("enrollment.detail.title")}
        subtitle={batch.data?.label || ""}
        actions={
          <Link to="/enrollment">
            <Button variant="secondary">{t("enrollment.detail.backToList")}</Button>
          </Link>
        }
      />

      {batch.isLoading && <LoadingState />}
      {batch.error && (
        <ErrorState
          titleKey="states.errorTitle"
          message={translateError(batch.error)}
        />
      )}

      {batch.data && (
        <div style={{ display: "grid", gap: 16 }}>
          <div className="stat-grid">
            <StatTile
              label={t("enrollment.detail.stat.total")}
              value={tokens.data?.length ?? batch.data.tokens_total ?? 0}
              sub={t(`enrollment.method.${batch.data.method}`)}
            />
            <StatTile
              label={t("enrollment.detail.stat.consumed")}
              value={tokens.data?.filter((tk) => tk.consumed_at !== null).length ?? 0}
              sub={t("enrollment.detail.stat.consumedSub")}
            />
            <StatTile
              label={t("enrollment.detail.stat.remaining")}
              value={
                tokens.data
                  ? tokens.data.filter((tk) => tk.consumed_at === null).length
                  : 0
              }
              sub={t("enrollment.detail.stat.remainingSub")}
            />
            <StatTile
              label={t("enrollment.list.column.expiresAt")}
              value={
                batch.data.expires_at
                  ? new Date(batch.data.expires_at).toLocaleDateString()
                  : "—"
              }
              sub={
                batch.data.is_revoked
                  ? t("enrollment.status.revoked")
                  : t("enrollment.status.active")
              }
            />
          </div>

          {!batch.data.is_revoked && (
            <Card title={t("enrollment.detail.issueTokens")}>
              <CreateTokensForm
                batchId={id}
                onIssued={(issued) => {
                  setJustIssued(issued);
                  void qc.invalidateQueries({
                    queryKey: ["enrollment", "batch", id, "tokens"],
                  });
                  void qc.invalidateQueries({ queryKey: ["enrollment", "batches"] });
                }}
              />
            </Card>
          )}

          {justIssued.length > 0 && (
            <Card title={t("enrollment.detail.justIssued")}>
              <Alert tone="warning" title={t("enrollment.detail.oneTimeWarningTitle")}>
                {t("enrollment.detail.oneTimeWarningBody")}
              </Alert>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
                {justIssued.map((tok) => (
                  <div key={tok.id} style={{ borderTop: "1px dashed var(--color-border)", paddingTop: 16 }}>
                    <div className="token-row__meta">
                      {t("enrollment.tokens.column.shortCode")}: {tok.short_code}
                    </div>

                    <div className="token-row__meta">{t("enrollment.detail.rawToken")}</div>
                    <div className="token-row">
                      <div className="token-row__value">
                        <div className="code-block">{tok.raw_token}</div>
                      </div>
                      <CopyButton value={tok.raw_token} />
                    </div>

                    <div className="token-row__meta">{t("enrollment.detail.enrollmentUrl")}</div>
                    <div className="token-row">
                      <div className="token-row__value">
                        <div className="code-block">{tok.enrollment_url}</div>
                      </div>
                      <CopyButton value={tok.enrollment_url} />
                    </div>

                    <div className="token-row__meta">{t("enrollment.detail.qrPayload")}</div>
                    <div className="token-row">
                      <div className="token-row__value">
                        <div className="code-block">
                          {JSON.stringify(tok.qr_payload, null, 2)}
                        </div>
                      </div>
                      <CopyButton value={JSON.stringify(tok.qr_payload, null, 2)} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card title={t("enrollment.detail.tokensList")} padding="none">
            {tokens.isLoading && <LoadingState />}
            {tokens.error && (
              <ErrorState
                titleKey="states.errorTitle"
                message={translateError(tokens.error)}
              />
            )}
            {tokens.data && (
              <Table
                rows={tokens.data}
                columns={columns}
                rowKey={(row) => row.id}
                empty={
                  <EmptyState
                    titleKey="enrollment.detail.emptyTokensTitle"
                    bodyKey="enrollment.detail.emptyTokensBody"
                  />
                }
              />
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
